/** Provides administrator review and remediation workflows for audited product images. */
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import axios from "axios";
import {useRecoilValue} from "recoil";
import {
   Alert,
   Box,
   Button,
   Chip,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   IconButton,
   Stack,
   TextField,
   Tooltip,
   useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import EditIcon from "@mui/icons-material/Edit";
import CompareIcon from "@mui/icons-material/Compare";
import BlockIcon from "@mui/icons-material/Block";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {BodyText, TitleText} from "../../../layout/Typography";
import {RangeList} from "../../../components/rangelist/RangeList";
import {RangeListColumn, RangeListResponse} from "../../../components/rangelist/RangeListTypes";
import {Thumbnail} from "../../../components/Thumbnail";
import {Dialogs} from "../../../DialogProvider";
import {ImageEditor} from "../../../components/imageeditor/ImageEditor";
import {CurrentUserIsAdmin} from "../../../atoms/CurrentUser";
import {BrandItemPagePath} from "../../brand/brandItem/BrandItemPage";
import {ImageAuditBulkApprove} from "./ImageAuditBulkApprove";
import {
   BrandItemImagesRequireAuditQuery,
   useApproveImageAuditMutation,
   useBrandItemImageClearRemoveBgResultMutation,
   useBrandItemImagesRequireAuditLazyQuery,
   useHideImageAuditMutation,
   useRejectImageAuditMutation,
   useRejectImageAuditRetakeMutation
} from "../../../Queries";
import "react-advanced-cropper/dist/style.css";

type BrandItemImageAudit = NonNullable<NonNullable<BrandItemImagesRequireAuditQuery['BrandItemImagesRequireAudit']>[number]>

type BrandItemImageUploadResponse = {
   imageId?: string
}

const sortAuditImages = (images: BrandItemImageAudit[]) => {
   return [...images].sort((a, b) => {
      const sortOrderDiff = (a.sortOrder || 0) - (b.sortOrder || 0);
      if (sortOrderDiff !== 0) {
         return sortOrderDiff;
      }

      return String(a._id).localeCompare(String(b._id));
   });
}

const uploadToBrandItem = async (brandHouseId: string, file: Blob) => {
   const formData = new FormData();
   formData.append("file", file, Date.now() + ".png");

   const {data} = await axios.post<BrandItemImageUploadResponse>(
      import.meta.env.VITE_BRANDITEMIMAGE + "/" + brandHouseId,
      formData
   );

   if (!data?.imageId) {
      throw new Error("Image upload did not return an imageId.");
   }

   return data.imageId;
}

/** Displays the pending image queue with individual and bulk audit actions. */
export const ImageAuditPage = () => {
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);
   const theme = useTheme();
   const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
   const [auditImages, setAuditImages] = useState<BrandItemImageAudit[]>([]);
   const [compareIndex, setCompareIndex] = useState<number | null>(null);
   const [editingImage, setEditingImage] = useState<BrandItemImageAudit | null>(null);
   const [search, setSearch] = useState("");
   const [refetchKey, setRefetchKey] = useState(0);
   const [bulkApproving, setBulkApproving] = useState(false);

   const [fetchAuditImages] = useBrandItemImagesRequireAuditLazyQuery({fetchPolicy: "no-cache"});
   const [approveImageAudit, {loading: approving}] = useApproveImageAuditMutation();
   const [rejectImageAudit, {loading: rejecting}] = useRejectImageAuditMutation();
   const [rejectImageAuditRetake, {loading: retaking}] = useRejectImageAuditRetakeMutation();
   const [hideImageAudit, {loading: hiding}] = useHideImageAuditMutation();
   const [clearRemoveBgResult, {loading: clearing}] = useBrandItemImageClearRemoveBgResultMutation();

   const activeImage = compareIndex !== null ? auditImages[compareIndex] : null;
   const actionBusy = approving || rejecting || retaking || hiding || clearing || bulkApproving;

   const loadAuditImages = useCallback(async () => {
      const {data} = await fetchAuditImages();

      const images = sortAuditImages(
         (data?.BrandItemImagesRequireAudit || [])
            .filter((image): image is BrandItemImageAudit => !!image)
      );
      setAuditImages(images);
      return images;
   }, [fetchAuditImages]);

   /** Approves one image without reloading so the bulk action can refresh only once. */
   const approveImageById = useCallback((imageId: string) => {
      return approveImageAudit({variables: {brandItemImageId: imageId}});
   }, [approveImageAudit]);

   /** Resets open review state and reloads the queue after bulk approval. */
   const refreshAfterBulkApproval = useCallback(async () => {
      setCompareIndex(null);
      await loadAuditImages();
      setRefetchKey(prev => prev + 1);
   }, [loadAuditImages]);

   const advanceAfterAction = useCallback(async (processedImage: BrandItemImageAudit) => {
      const previousIndex = auditImages.findIndex(image => image._id === processedImage._id);
      const nextImageId = auditImages[previousIndex + 1]?._id || auditImages[previousIndex - 1]?._id;
      const images = await loadAuditImages();

      setRefetchKey(prev => prev + 1);

      if (images.length === 0) {
         setCompareIndex(null);
         return;
      }

      if (nextImageId) {
         const nextIndex = images.findIndex(image => image._id === nextImageId);
         if (nextIndex >= 0) {
            setCompareIndex(nextIndex);
            return;
         }
      }

      setCompareIndex(Math.min(Math.max(previousIndex, 0), images.length - 1));
   }, [auditImages, loadAuditImages]);

   const onRequestData = useCallback(async (
      _search: string | undefined,
      _sort: string | undefined,
      page: number,
      perPage: number
   ): Promise<RangeListResponse<BrandItemImageAudit>> => {
      const images = await loadAuditImages();
      const searchValue = search.trim().toLowerCase();
      const filtered = searchValue ? images.filter(image => {
         return String(image._id).toLowerCase().includes(searchValue)
            || (image.brandItemId || "").toLowerCase().includes(searchValue);
      }) : images;
      const startIndex = (page - 1) * perPage;

      return {
         data: filtered.slice(startIndex, startIndex + perPage),
         totalCount: filtered.length
      };
   }, [loadAuditImages, search]);

   const openCompare = useCallback((image: BrandItemImageAudit) => {
      const index = auditImages.findIndex(candidate => candidate._id === image._id);
      setCompareIndex(index >= 0 ? index : 0);
   }, [auditImages]);

   // Runs a queue action then advances to the next item. Intentionally no success
   // snackbar/modal so the review loop isn't interrupted; buttons stay disabled via
   // actionBusy while in flight, and GraphQL errors surface through the global handler.
   const runAction = async (image: BrandItemImageAudit, action: () => Promise<unknown>) => {
      try {
         await action();
         await advanceAfterAction(image);
      } catch (error) {
         // GraphQL errors are surfaced globally by ApolloApp's errorLink / ErrorHandler.
      }
   }

   const approve = (image: BrandItemImageAudit) => runAction(image, () =>
      approveImageAudit({variables: {brandItemImageId: image._id}})
   );

   const hide = (image: BrandItemImageAudit) => runAction(image, () =>
      hideImageAudit({variables: {brandItemImageId: image._id}})
   );

   const rejectAndRetake = (image: BrandItemImageAudit) => {
      Dialogs.confirm({
         title: "Reject and request new photos?",
         subtitle: "This pauses the brand item with a \"Retake photos\" problem. The item stays blocked from going live until the photos are retaken and the problem is resolved.",
         defaultNo: true,
         buttonYes: "Reject & pause item",
         buttonNo: "Cancel"
      }, () => runAction(image, () =>
         rejectImageAuditRetake({variables: {brandItemImageId: image._id}})
      ));
   }

   const clearAndRerun = (image: BrandItemImageAudit) => runAction(image, () =>
      clearRemoveBgResult({variables: {brandItemImageId: image._id}})
   );

   const saveEditedImage = async (image: BrandItemImageAudit, blob: Blob) => {
      if (!image.brandHouseId) {
         return;
      }

      try {
         const uploadId = await uploadToBrandItem(image.brandHouseId, blob);
         await rejectImageAudit({
            variables: {
               brandItemImageId: image._id,
               uploadId
            }
         });
         setEditingImage(null);
         await advanceAfterAction(image);
      } catch (error) {
         // The image upload is a REST call, so it bypasses the global Apollo error
         // handler — surface failures here so the operator can retry.
         Dialogs.info({
            title: "Could not save edited image",
            subtitle: "Uploading the edited image failed. Please try again."
         });
      }
   }

   const columns = useMemo<RangeListColumn<BrandItemImageAudit>[]>(() => [
      {
         id: "uploadUrl",
         label: "Original",
         width: 2,
         minHeight: 86,
         get: row => row.uploadUrl,
         render: (url) => <Thumbnail size={72} image={url} hideBorder/>
      },
      {
         id: "imageUrl",
         label: "Processed",
         width: 2,
         minHeight: 86,
         get: row => row.imageUrl,
         render: (url) => <Thumbnail size={72} image={url} hideBorder/>
      },
      {
         id: "brandItemId",
         label: "Brand item",
         width: 4,
         field: "brandItemId",
         ellipsis: true,
         render: (value) => value || "-"
      },
      {
         id: "status",
         label: "Audit",
         width: 3,
         get: row => row.requireAudit,
         render: (_value, row) => (
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
               <Chip size={"small"} label={"Requires audit"} color={"warning"} variant={"outlined"}/>
               <Button
                  size={"small"}
                  variant={"outlined"}
                  startIcon={<CompareIcon/>}
                  onClick={(event) => {
                     event.preventDefault();
                     event.stopPropagation();
                     if (row) {
                        openCompare(row);
                     }
                  }}
               >
                  Review
               </Button>
            </Stack>
         )
      }
   ], [openCompare]);

   const stepCompare = (delta: number) => {
      if (compareIndex === null) {
         return;
      }
      const next = compareIndex + delta;
      if (next >= 0 && next < auditImages.length) {
         setCompareIndex(next);
      }
   }

   const renderCompare = () => {
      if (!activeImage) {
         return null;
      }

      return <Dialog open={true} onClose={() => setCompareIndex(null)} fullScreen={smallScreen} fullWidth maxWidth={"md"}>
         <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <Box>
               <TitleText type={"h3"}>Review background removal</TitleText>
               <BodyText type={"body2"} sx={{color: "#707070"}}>
                  Brand item {activeImage.brandItemId || "-"}
               </BodyText>
               {activeImage.brandHouseId && activeImage.brandItemId && (
                  <Button
                     size={"small"}
                     variant={"text"}
                     startIcon={<OpenInNewIcon/>}
                     sx={{paddingLeft: 0, marginTop: "2px"}}
                     onClick={() => window.open(
                        BrandItemPagePath(activeImage.brandHouseId, activeImage.brandItemId, false),
                        "_blank"
                     )}
                  >
                     Open item editor
                  </Button>
               )}
            </Box>
            <IconButton onClick={() => setCompareIndex(null)}><CloseIcon/></IconButton>
         </DialogTitle>
         <DialogContent>
            <Stack
               direction={smallScreen ? "column" : "row"}
               spacing={3}
               alignItems={"center"}
               justifyContent={"center"}
               sx={{minHeight: "calc(100vh - 210px)"}}
            >
               <Box sx={{textAlign: "center"}}>
                  <BodyText type={"body2"} sx={{marginBottom: "8px"}}>Original (with background)</BodyText>
                  <Thumbnail size={320} image={activeImage.uploadUrl || undefined}/>
               </Box>
               <Box sx={{textAlign: "center"}}>
                  <BodyText type={"body2"} sx={{marginBottom: "8px"}}>Processed (background removed)</BodyText>
                  <Thumbnail size={320} image={activeImage.imageUrl || undefined}/>
               </Box>
            </Stack>
         </DialogContent>
         <DialogActions sx={{alignItems: "center", gap: "8px", padding: "16px 24px"}}>
            <IconButton disabled={compareIndex === 0} onClick={() => stepCompare(-1)}><ChevronLeftIcon/></IconButton>
            <BodyText type={"body2"}>{compareIndex !== null ? compareIndex + 1 : 0} / {auditImages.length}</BodyText>
            <IconButton disabled={compareIndex === auditImages.length - 1} onClick={() => stepCompare(1)}>
               <ChevronRightIcon/>
            </IconButton>
            <Box sx={{flex: 1}}/>
            <Tooltip title={!activeImage.uploadUrl ? "Original image is missing" : ""}>
               <span>
                  <Button
                     variant={"outlined"}
                     startIcon={<EditIcon/>}
                     disabled={actionBusy || !activeImage.uploadUrl}
                     onClick={() => setEditingImage(activeImage)}
                  >
                     Edit
                  </Button>
               </span>
            </Tooltip>
            <Button
               variant={"outlined"}
               startIcon={<ReplayIcon/>}
               disabled={actionBusy}
               onClick={() => clearAndRerun(activeImage)}
            >
               Rerun
            </Button>
            <Button
               variant={"outlined"}
               startIcon={<VisibilityOffIcon/>}
               disabled={actionBusy}
               onClick={() => hide(activeImage)}
            >
               Hide 24h
            </Button>
            <Button
               variant={"contained"}
               color={"error"}
               startIcon={<BlockIcon/>}
               disabled={actionBusy}
               onClick={() => rejectAndRetake(activeImage)}
            >
               Reject
            </Button>
            <Button
               variant={"contained"}
               color={"success"}
               startIcon={<CheckCircleIcon/>}
               disabled={actionBusy}
               onClick={() => approve(activeImage)}
            >
               Approve
            </Button>
         </DialogActions>
      </Dialog>;
   }

   const renderEditor = () => {
      if (!editingImage?.uploadUrl) {
         return null;
      }

      return <Dialog open={true} onClose={() => setEditingImage(null)} fullWidth maxWidth={"md"}>
         <DialogContent sx={{padding: 0}}>
            <ImageEditor imageUrl={editingImage.uploadUrl} onSave={(blob) => saveEditedImage(editingImage, blob)}/>
         </DialogContent>
      </Dialog>;
   }

   if (!isAdministrator) {
      return <Page>
         <PageHeader title={"Image Audit"}/>
         <PageContent>
            <Alert severity={"error"}>This tool is available to administrators only.</Alert>
         </PageContent>
      </Page>;
   }

   return <Page>
      <PageHeader title={"Image Audit"}>
         <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <ImageAuditBulkApprove
               imageIds={auditImages.map(image => String(image._id))}
               approve={approveImageById}
               onComplete={refreshAfterBulkApproval}
               onBusyChange={setBulkApproving}
               disabled={actionBusy}
            />
            <TextField
               size={"small"}
               value={search}
               placeholder={"Search image or brand item"}
               onChange={event => {
                  setSearch(event.target.value);
                  setRefetchKey(prev => prev + 1);
               }}
            />
         </Stack>
      </PageHeader>
      <PageContent>
         <RangeList
            key={refetchKey}
            globalId={"imageAuditList"}
            columns={columns}
            itemsPerPage={50}
            onRequestData={onRequestData}
            onSelectRow={openCompare}
            options={{
               idField: "_id",
               itemHeight: 96
            }}
         />
      </PageContent>
      {renderCompare()}
      {renderEditor()}
   </Page>;
}
